import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMap, FeatureGroup } from "react-leaflet";
import L, { latLng } from "leaflet";
import type { RealEstateSearchResult } from "../../types";
import type { AdditionalFieldConfig } from "@/features/accountSettings/types/additionalFields";
import { createRoot } from "react-dom/client";
import { flushSync } from "react-dom";
import { formatArea } from "../../utils";
import { NYHalfWidthNumberToFillWidthNumber } from "@/utils/utils";
import {
  Box,
  CircularProgress,
  FormControl,
  FormLabel,
  Grid,
  Link,
  Typography,
  ThemeProvider,
} from "@mui/material";
import { theme } from "@/types/theme";
import {
  Button,
  ButtonVariantOption,
  ButtonColorOption,
} from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import PlaceIcon from "@mui/icons-material/Place";
import ImportContactsIcon from "@mui/icons-material/ImportContacts";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import FactoryIcon from "@mui/icons-material/Factory";
import HotelIcon from "@mui/icons-material/Hotel";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import LocalParkingIcon from "@mui/icons-material/LocalParking";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import { REAL_ESTATE_TYPE_MAP } from "../MapSearchDialog";
import type { ChibanInput } from "@/types/kaokuNumber";
import {
  addRealEstateSearchTagUpdateListener,
  dispatchRealEstateSearchTagUpdate,
} from "../../utils/tagSync/realEstateSearchTagSync";
import {
  addRealEstateSearchRemarksUpdateListener,
  dispatchRealEstateSearchRemarksUpdate,
} from "../../utils/tagSync/realEstateSearchRemarksSync";
import {
  addRealEstateSearchAdditionalFieldsUpdateListener,
  dispatchRealEstateSearchAdditionalFieldsUpdate,
} from "../../utils/tagSync/realEstateSearchAdditionalFieldsSync";
import { MemoModal } from "../MemoModal";
import { OtherButtonModal } from "../OtherButtonModal";
import { useAdditionalFieldsConfig } from "../../hooks/useAdditionalFieldsConfig";
import { getGoogleMapsUrl } from "../../utils/openGoogleMaps";
import { getChikaMapUrl } from "../../utils/openChikaMap";
import { AddressIntegrator } from "@/utils/AddressIntegrator";
import { useFeatureFlags } from "@/configs/featureFlag";
import { type RealEstateRequestModalHandler } from "../../types/realEstateRequest";
import { CanvasPinMarker } from "../../utils/canvasPin/CanvasPinMarker";
import { getSharedCanvasPinRenderer } from "../../utils/canvasPin/sharedCanvasPinRenderer";
import {
  PIN_COLORS,
  getTeardropPinIcon,
} from "../../utils/canvasPin/teardropPin";
import type { RealEstateSearchAddressResult } from "../../api/getRealEstateSearchAddress";
import { needsLandAddress } from "../../utils/realEstateSearch/landAddress";
import { getRealEstateSearchPinsKey } from "../../utils/realEstateSearch/searchResultPinsKey";

/** 土地の住居表示を取る関数（ChibanViewer が取得と一覧への反映をまとめて行う） */
type LoadLandAddress = (
  item: RealEstateSearchResult
) => Promise<RealEstateSearchAddressResult>;

interface RealEstateSearchLayerProps {
  defaultVisible: boolean;
  realEstateSearchData: RealEstateSearchResult[];
  handleKaokuNumberSearch?: (chibanList: ChibanInput[]) => void;
  kaokuNumberSearchFlag: boolean;
  onTagsChange?: (index: number, tags: string[]) => void;
  onRemarksChange?: (index: number, remarks: string) => void;
  onAdditionalFieldsChange?: (index: number, additionalFields: string) => void;
  onPinClick?: (index: number) => void;
  handlePinSave?: () => void;
  crm?: boolean;
  visible?: boolean;
  // 役所資料取得依頼
  realEstateDocumentFetchFlag?: boolean;
  handleRealEstateDocumentFetchModal?: RealEstateRequestModalHandler;
  handleRealEstateResearchRequestModal?: RealEstateRequestModalHandler;
  // 物件概要書
  propertySummary?: boolean;
  handleNewPropertySheet?: (adi: AddressIntegrator) => void;
  /** 住居表示が空の土地のポップアップを開いた時に、住居表示を取る */
  loadLandAddress?: LoadLandAddress;
}

export const REAL_ESTATE_SEARCH_LAYER_NAME = "不動産検索の結果を表示する";

export const RealEstateSearchLayer: React.FC<RealEstateSearchLayerProps> = ({
  defaultVisible,
  realEstateSearchData,
  handleKaokuNumberSearch,
  kaokuNumberSearchFlag,
  onTagsChange,
  onRemarksChange,
  onAdditionalFieldsChange,
  onPinClick,
  handlePinSave,
  crm = false,
  visible: visibleProp,
  realEstateDocumentFetchFlag = false,
  handleRealEstateDocumentFetchModal,
  handleRealEstateResearchRequestModal,
  propertySummary = false,
  handleNewPropertySheet,
  loadLandAddress,
}) => {
  const map = useMap();
  const layerRef = useRef<L.LayerGroup>(L.layerGroup());
  // ピンは検索結果の物件が変わった時にしか作り直さないので、住居表示を取る関数は ref で最新のものを呼ぶ
  const loadLandAddressRef = useRef(loadLandAddress);
  loadLandAddressRef.current = loadLandAddress;
  const [visible, setVisible] = useState(defaultVisible);
  const { availableFields, hasAvailableFields } = useAdditionalFieldsConfig();
  const { hotelSearch, dormitorySearch, priceMap, industrialFacilitySearch } =
    useFeatureFlags();
  const hotelSearchFlag = Boolean(hotelSearch);
  const dormitorySearchFlag = Boolean(dormitorySearch);
  const priceMapFlag = Boolean(priceMap);
  const industrialFacilitySearchFlag = Boolean(industrialFacilitySearch);

  useEffect(() => {
    if (!map) return;

    if (typeof visibleProp === "boolean") {
      setVisible(visibleProp);
      return;
    }

    const handleOverlayAdd = (e: L.LayersControlEvent): void => {
      if (e.name === REAL_ESTATE_SEARCH_LAYER_NAME) {
        setVisible(true);
      }
    };

    const handleOverlayRemove = (e: L.LayersControlEvent): void => {
      if (e.name === REAL_ESTATE_SEARCH_LAYER_NAME) {
        setVisible(false);
      }
    };

    map.on("overlayadd", handleOverlayAdd);
    map.on("overlayremove", handleOverlayRemove);

    return () => {
      map.off("overlayadd", handleOverlayAdd);
      map.off("overlayremove", handleOverlayRemove);
    };
  }, [map, visibleProp]);

  useEffect(() => {
    if (typeof visibleProp === "boolean") {
      setVisible(visibleProp);
    }
  }, [visibleProp]);

  // realEstateSearchDataへの参照を保持
  const dataRef = useRef<RealEstateSearchResult[]>(realEstateSearchData);
  dataRef.current = realEstateSearchData;
  // 地図を開いたまま R.E.REPO のフラグが切り替わったら、ポップアップの「不動産調査依頼」の有無を作り直す
  // （handler は毎回作り直されるため、そのものではなく有無を依存にする）
  const hasResearchRequestHandler = Boolean(
    handleRealEstateResearchRequestModal
  );
  // 検索し直して物件が変わった時だけピンを作り直す（件数が同じでも作り直す）。
  // 住居表示・タグなどの書き足しでは作り直さない（開いているポップアップが閉じるため）
  const pinsKey = useMemo(
    () => getRealEstateSearchPinsKey(realEstateSearchData),
    [realEstateSearchData]
  );

  useEffect(() => {
    if (!map || !defaultVisible || !realEstateSearchData) return;

    if (layerRef.current) {
      layerRef.current.clearLayers();
    }

    if (realEstateSearchData.length) {
      const markers = realEstateSearchData.map((plot, index) =>
        createRealEstateSearchMarker(
          map,
          () => dataRef.current[index], // 最新データを取得する関数を渡す
          index,
          kaokuNumberSearchFlag,
          handleKaokuNumberSearch,
          onTagsChange,
          onRemarksChange,
          onAdditionalFieldsChange,
          onPinClick,
          handlePinSave,
          crm,
          availableFields,
          hasAvailableFields,
          realEstateDocumentFetchFlag,
          handleRealEstateDocumentFetchModal,
          handleRealEstateResearchRequestModal,
          propertySummary,
          handleNewPropertySheet,
          hotelSearchFlag,
          dormitorySearchFlag,
          priceMapFlag,
          industrialFacilitySearchFlag,
          async (item) =>
            (await loadLandAddressRef.current?.(item)) ?? { kind: "error" }
        )
      );
      // canvas は追加した順に重ねて描くため、L.Marker と同じく南のピンが上に来るよう北から並べる
      markers
        .sort((a, b) => b.getLatLng().lat - a.getLatLng().lat)
        .forEach((marker) => {
          layerRef.current?.addLayer(marker);
        });
    }

    if (visible) {
      layerRef.current.addTo(map);
    } else {
      map.removeLayer(layerRef.current);
    }
  }, [map, visible, pinsKey, hasResearchRequestHandler]);

  return <FeatureGroup />;
};

/** ポップアップで取る土地の住居表示の状態 */
type LandAddressState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "found"; address: string }
  | {
      status: "failed";
      reason: Exclude<RealEstateSearchAddressResult["kind"], "found">;
    };

/** 取れなかった理由ごとの案内。再取得で取れる見込みがあるものだけ再取得を出す */
const LAND_ADDRESS_FAILURE: Record<
  Extract<LandAddressState, { status: "failed" }>["reason"],
  { message: string; retryable: boolean }
> = {
  rateLimited: {
    message: "取得が集中しています。少し待ってから再取得してください",
    retryable: true,
  },
  unavailable: {
    message: "住居表示を取得できませんでした。時間をおいて再取得してください",
    retryable: true,
  },
  forbidden: {
    message: "住居表示を取得できませんでした",
    retryable: false,
  },
  error: {
    message: "住居表示を取得できませんでした",
    retryable: true,
  },
};

/** 住居表示がまだ無い土地の、住居表示欄（取得中・住居表示なし・取れなかった時） */
const LandAddressStatus: React.FC<{
  state: LandAddressState;
  onRetry: () => void;
}> = ({ state, onRetry }) => {
  if (state.status === "idle" || state.status === "loading") {
    return (
      <Box
        component="span"
        sx={{ display: "inline-flex", gap: 1, alignItems: "center" }}
      >
        <CircularProgress size={12} />
        取得中…
      </Box>
    );
  }
  if (state.status === "found") {
    // 海上・山林など、その座標に住居表示が無い
    return (
      <Box component="span" sx={{ color: "text.secondary" }}>
        住居表示はありません
      </Box>
    );
  }
  const { message, retryable } = LAND_ADDRESS_FAILURE[state.reason];
  return (
    <Box component="span" sx={{ color: "text.secondary" }}>
      {message}
      {retryable && (
        <>
          {" "}
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={(event) => {
              // 押すとこのリンクが消えて、Leaflet がポップアップの中のクリックと判定できず
              // 地図のクリック（地番検索）として扱ってしまうので、地図まで伝えない
              event.stopPropagation();
              onRetry();
            }}
          >
            再取得
          </Link>
        </>
      )}
    </Box>
  );
};

/**
 * 不動産検索結果のポップアップコンテンツコンポーネント
 */
const RealEstateSearchPopupContent: React.FC<{
  map: L.Map;
  item: RealEstateSearchResult;
  index: number;
  kaokuNumberSearchFlag: boolean;
  handleKaokuNumberSearch?: (chibanList: ChibanInput[]) => void;
  onTagsChange?: (index: number, tags: string[]) => void;
  onRemarksChange?: (index: number, remarks: string) => void;
  onAdditionalFieldsChange?: (index: number, additionalFields: string) => void;
  handlePinSave?: () => void;
  crm: boolean;
  popupWidth: number;
  availableFields: AdditionalFieldConfig | undefined;
  hasAvailableFields: boolean;
  // 役所資料取得依頼
  realEstateDocumentFetchFlag: boolean;
  handleRealEstateDocumentFetchModal?: RealEstateRequestModalHandler;
  handleRealEstateResearchRequestModal?: RealEstateRequestModalHandler;
  // 物件概要書
  propertySummary: boolean;
  handleNewPropertySheet?: (adi: AddressIntegrator) => void;
  hotelSearchFlag: boolean;
  dormitorySearchFlag: boolean;
  priceMap: boolean;
  industrialFacilitySearchFlag: boolean;
  /** 住居表示が空の土地の住居表示を取る（無ければ取れなかった扱い） */
  loadLandAddress?: LoadLandAddress;
  /** 中身の大きさが変わった時（住居表示が届いた時など）。ポップアップの大きさ・位置を測り直す */
  onLayoutChange: () => void;
}> = ({
  map,
  item,
  index,
  kaokuNumberSearchFlag,
  handleKaokuNumberSearch,
  onTagsChange,
  onRemarksChange,
  onAdditionalFieldsChange,
  handlePinSave,
  crm,
  popupWidth,
  availableFields,
  hasAvailableFields,
  realEstateDocumentFetchFlag,
  handleRealEstateDocumentFetchModal,
  handleRealEstateResearchRequestModal,
  propertySummary,
  handleNewPropertySheet,
  hotelSearchFlag,
  dormitorySearchFlag,
  priceMap,
  industrialFacilitySearchFlag,
  loadLandAddress,
  onLayoutChange,
}) => {
  // 土地の住居表示は検索時には取らず、ポップアップを初めて開いた時に取る（Google の回数制限を避けるため）。
  // 中身はポップアップを閉じても残るので、開き直しても取り直さない
  // 検索し直して物件が変わればピンごと作り直すが、念のため取得の状態はどの土地のものかを持ち、
  // 今の土地のものだけを使う（別の土地の住居表示を出さないため）
  const landKey = `${item.prefName}|${item.location}|${item.chibanKaokuNumber}|${item.latitude},${item.longitude}`;
  const [landAddressOf, setLandAddressOf] = useState<{
    key: string;
    state: LandAddressState;
  }>({ key: landKey, state: { status: "idle" } });
  const landAddress: LandAddressState =
    landAddressOf.key === landKey ? landAddressOf.state : { status: "idle" };
  const fetchLandAddress = useCallback((): void => {
    const key = landKey;
    if (!loadLandAddress) {
      setLandAddressOf({ key, state: { status: "failed", reason: "error" } });
      return;
    }
    setLandAddressOf({ key, state: { status: "loading" } });
    loadLandAddress(item)
      .then((result) => {
        setLandAddressOf({
          key,
          state:
            result.kind === "found"
              ? { status: "found", address: result.address }
              : { status: "failed", reason: result.kind },
        });
      })
      .catch(() => {
        setLandAddressOf({ key, state: { status: "failed", reason: "error" } });
      });
  }, [loadLandAddress, item, landKey]);
  useEffect(() => {
    if (needsLandAddress(item) && landAddress.status === "idle") {
      fetchLandAddress();
    }
  }, [item, landAddress.status, fetchLandAddress]);
  useEffect(() => {
    if (landAddressOf.state.status !== "idle") onLayoutChange();
  }, [landAddressOf, onLayoutChange]);
  /** 表示・物件概要書・役所資料取得依頼・不動産調査依頼に使う住居表示（まだ取れていなければ空） */
  const address =
    item.address !== ""
      ? item.address
      : landAddress.status === "found"
      ? landAddress.address
      : "";

  // メモモーダル（タグ・備考・カスタム項目編集）の表示状態
  const [memoModalOpen, setMemoModalOpen] = useState(false);
  // その他操作モーダルの表示状態
  const [otherButtonModalOpen, setOtherButtonModalOpen] = useState(false);

  // タグ・備考・カスタム項目の現在値を管理（モーダルに渡すため）
  const [currentTags, setCurrentTags] = useState(item.tags ?? []);
  const [currentRemarks, setCurrentRemarks] = useState(item.remarks ?? "");
  const [currentAdditionalFields, setCurrentAdditionalFields] = useState(
    item.additionalFields ?? ""
  );

  // itemが変更されたときに現在値を更新
  useEffect(() => {
    setCurrentTags(item.tags ?? []);
    setCurrentRemarks(item.remarks ?? "");
    setCurrentAdditionalFields(item.additionalFields ?? "");
  }, [
    item.landId,
    item.buildingId,
    item.tags,
    item.remarks,
    item.additionalFields,
  ]);

  // タグ更新イベントリスナー（PropertySelectionGridでタグが変更された後、ポップアップが開かれた際に最新のタグを反映）
  useEffect(() => {
    const cleanup = addRealEstateSearchTagUpdateListener(index, (newTags) => {
      setCurrentTags(newTags);
    });

    return cleanup;
  }, [index]);

  // 備考更新イベントリスナー（PropertySelectionGridで備考が変更された後、ポップアップが開かれた際に最新の備考を反映）
  useEffect(() => {
    const cleanup = addRealEstateSearchRemarksUpdateListener(
      index,
      (newRemarks) => {
        setCurrentRemarks(newRemarks);
      }
    );

    return cleanup;
  }, [index]);

  // 追加項目更新イベントリスナー
  useEffect(() => {
    const cleanup = addRealEstateSearchAdditionalFieldsUpdateListener(
      index,
      (newAdditionalFields) => {
        setCurrentAdditionalFields(newAdditionalFields);
      }
    );

    return cleanup;
  }, [index]);

  // 都道府県・市区町村・地番/家屋番号が全て揃っているか
  const hasRequiredLocationInfo: boolean =
    item.prefName !== "" &&
    item.cityName !== "" &&
    item.chibanKaokuNumber !== "";

  // 物件概要書ボタンの有効条件（住居表示・都道府県・市区町村・地番が全て必要）
  // 現時点では土地からの流れのみ対応しているため、土地以外は無効化
  const canShowPropertySummaryButton: boolean =
    item.bookType === "LAND" && hasRequiredLocationInfo && address !== "";

  const locationWithoutCity = item.location.replace(item.cityName, "");
  const locationWithCity = `${item.cityName}${locationWithoutCity}`;

  const handleClickKaokuNumberSearchButton = (): void => {
    if (!handleKaokuNumberSearch) return;

    const c: ChibanInput = {
      prefectureName: item.prefName,
      location: locationWithCity,
      chiban: item.chibanKaokuNumber,
      bookType: item.bookType,
      latitude: item.latitude,
      longitude: item.longitude,
    };
    handleKaokuNumberSearch([c]);
  };

  const getRealEstateTypeForUrl = (realEstateType: string): string => {
    if (realEstateType === REAL_ESTATE_TYPE_MAP.building.label) {
      return "BUILDING";
    } else if (realEstateType === REAL_ESTATE_TYPE_MAP.condominium.label) {
      return "CONDOMINIUM";
    } else if (realEstateType === REAL_ESTATE_TYPE_MAP.land.label) {
      return "LAND";
    } else {
      return "ONE_BUILDING";
    }
  };

  const isLandProperty = item.bookType === "LAND";
  const chibanKaokuNumberLabel = isLandProperty ? "地番" : "家屋番号";
  // Google Maps（衛星写真）のURL
  const googleMapsUrl = getGoogleMapsUrl(map, {
    lat: item.latitude,
    lng: item.longitude,
  });

  // 地価マップのURL
  const chikaMapUrl = getChikaMapUrl({
    lat: item.latitude,
    lng: item.longitude,
  });

  // 役所資料取得依頼のハンドラー
  const handleRealEstateDocumentFetch =
    handleRealEstateDocumentFetchModal && hasRequiredLocationInfo
      ? () => {
          handleRealEstateDocumentFetchModal(
            item.prefName,
            address,
            item.chibanKaokuNumber,
            locationWithCity,
            true,
            item.bookType
          );
        }
      : undefined;

  const handleRealEstateResearchRequest =
    handleRealEstateResearchRequestModal && hasRequiredLocationInfo
      ? () => {
          handleRealEstateResearchRequestModal(
            item.prefName,
            address,
            item.chibanKaokuNumber,
            locationWithCity,
            true,
            item.bookType
          );
        }
      : undefined;

  // 物件概要書作成のハンドラー
  const handleCreatePropertySheet =
    handleNewPropertySheet && canShowPropertySummaryButton
      ? () => {
          // locationからcityNameを除いた部分をooazaNameとして設定
          // 例: location="さいたま市南区南本町2丁目", cityName="さいたま市南区" の場合、ooazaName="南本町2丁目"
          const ooazaName = item.location.startsWith(item.cityName)
            ? item.location.slice(item.cityName.length)
            : item.location;
          const adi = new AddressIntegrator({
            prefName: item.prefName,
            cityName: item.cityName,
            ooazaName,
            address,
            addressChiban: `${item.prefName}${item.location}${item.chibanKaokuNumber}`,
            chiban: item.chibanKaokuNumber,
            latlng: latLng(item.latitude, item.longitude),
            area: item.area,
          });
          handleNewPropertySheet(adi);
        }
      : undefined;

  // // 地図を表示しているコンテナの横幅
  // const mapContainerSizeX = map.getSize().x;

  // // 地図コンテナ幅375px以下では（300px幅のポップアップ＋余白）がきれいに収まらないので、ポップアップ幅を縮小する
  // if (mapContainerSizeX < 375) {
  //   // PopupContent.tsxと同じに0.8倍にすると家屋番号検索ボタンが改行されてしまうため、0.85倍に変更
  //   popupWidth = Math.floor(mapContainerSizeX * 0.85);
  // }

  return (
    <Box sx={{ width: popupWidth }}>
      <FormControl>
        <FormLabel>
          <PlaceIcon fontSize={"inherit"} />
          住居表示
        </FormLabel>
        <Typography
          ml={2}
          my={1}
          fontSize={"0.8rem"}
          variant={"body1"}
          component={"span"}
        >
          {needsLandAddress({ bookType: item.bookType, address }) ? (
            <LandAddressStatus state={landAddress} onRetry={fetchLandAddress} />
          ) : (
            NYHalfWidthNumberToFillWidthNumber(`${address}`)
          )}
        </Typography>
        <FormLabel>
          <ImportContactsIcon fontSize={"inherit"} /> {chibanKaokuNumberLabel}
        </FormLabel>
        <Typography
          ml={2}
          my={1}
          fontSize={"0.8rem"}
          variant={"body1"}
          component={"span"}
        >
          {NYHalfWidthNumberToFillWidthNumber(
            `${item.prefName}${item.location}${item.chibanKaokuNumber}`
          )}
        </Typography>
        {item.buildingName && (
          <>
            <FormLabel>
              <LocationCityIcon fontSize={"inherit"} /> 建物名
            </FormLabel>
            <Typography
              ml={2}
              my={1}
              fontSize={"0.8rem"}
              variant={"body1"}
              component={"span"}
            >
              {item.buildingName}
            </Typography>
          </>
        )}
        {hotelSearchFlag && item.hotelName && (
          <>
            <FormLabel>
              <HotelIcon fontSize={"inherit"} /> ホテル名
            </FormLabel>
            <Typography
              ml={2}
              my={1}
              fontSize={"0.8rem"}
              variant={"body1"}
              component={"span"}
            >
              {item.hotelName}
            </Typography>
          </>
        )}
        {item.parkingLotName && (
          <>
            <FormLabel>
              <LocalParkingIcon fontSize={"inherit"} /> 駐車場名
            </FormLabel>
            <Typography
              ml={2}
              my={1}
              fontSize={"0.8rem"}
              variant={"body1"}
              component={"span"}
            >
              {item.parkingLotName}
            </Typography>
          </>
        )}
        {dormitorySearchFlag && item.dormitoryName && (
          <>
            <FormLabel>
              <HomeWorkIcon fontSize={"inherit"} /> 寮・社宅名
            </FormLabel>
            <Typography
              ml={2}
              my={1}
              fontSize={"0.8rem"}
              variant={"body1"}
              component={"span"}
            >
              {item.dormitoryName}
            </Typography>
          </>
        )}
        {dormitorySearchFlag && item.dormitoryKind && (
          <>
            <FormLabel>
              <LocationCityIcon fontSize={"inherit"} /> 種別
            </FormLabel>
            <Typography
              ml={2}
              my={1}
              fontSize={"0.8rem"}
              variant={"body1"}
              component={"span"}
            >
              {item.dormitoryKind}
            </Typography>
          </>
        )}
        {industrialFacilitySearchFlag && item.industrialFacilityName && (
          <>
            <FormLabel>
              <FactoryIcon fontSize={"inherit"} /> 工場/倉庫名
            </FormLabel>
            <Typography
              ml={2}
              my={1}
              fontSize={"0.8rem"}
              variant={"body1"}
              component={"span"}
            >
              {item.industrialFacilityName}
            </Typography>
          </>
        )}
        <FormLabel>
          <SquareFootIcon fontSize={"inherit"} />
          推計地積(㎡)
        </FormLabel>
        <Typography
          ml={2}
          my={1}
          fontSize={"0.8rem"}
          variant={"body1"}
          component={"span"}
        >
          {formatArea(item.area)}
        </Typography>
      </FormControl>
      <Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Button
              label={"登記を取得"}
              disabled={!hasRequiredLocationInfo}
              onClick={() => {
                let url = "/acquirebook";
                if (item.prefName && item.location && item.chibanKaokuNumber) {
                  url =
                    url +
                    "?" +
                    `prefecture=${item.prefName}` +
                    `&city=${item.cityName}` +
                    `&location=${NYHalfWidthNumberToFillWidthNumber(
                      locationWithoutCity
                    )}` +
                    `&chiban=${NYHalfWidthNumberToFillWidthNumber(
                      item.chibanKaokuNumber
                    )}` +
                    `&realEstate=${getRealEstateTypeForUrl(
                      item.realEstateType
                    )}`;
                }
                window.open(url, "_blank");
              }}
              size={SizingWrapperStyle.INHERIT}
              variant={ButtonVariantOption.Contained}
              color={ButtonColorOption.Primary}
            />
          </Grid>
          <Grid item xs={crm ? 6 : 12}>
            <Button
              label={"その他操作"}
              onClick={() => {
                setOtherButtonModalOpen(true);
              }}
              size={SizingWrapperStyle.INHERIT}
              variant={ButtonVariantOption.Outlined}
              color={ButtonColorOption.Primary}
            />
          </Grid>
          {crm && (
            <Grid item xs={6}>
              <Button
                label={"メモ"}
                onClick={() => {
                  setMemoModalOpen(true);
                }}
                size={SizingWrapperStyle.INHERIT}
                variant={ButtonVariantOption.Outlined}
                color={ButtonColorOption.Primary}
                startIcon={<NoteAltIcon />}
              />
            </Grid>
          )}
        </Grid>
      </Box>
      <MemoModal
        open={memoModalOpen}
        onClose={() => {
          setMemoModalOpen(false);
        }}
        initialTags={currentTags}
        initialRemarks={currentRemarks}
        initialAdditionalFields={currentAdditionalFields}
        onTagsChange={(newTags) => {
          setCurrentTags(newTags);
          // 同期イベントを発火
          dispatchRealEstateSearchTagUpdate(index, newTags);
          // 親コンポーネントへの通知
          if (onTagsChange) {
            onTagsChange(index, newTags);
          }
        }}
        onRemarksChange={(newRemarks) => {
          setCurrentRemarks(newRemarks);
          // 同期イベントを発火
          dispatchRealEstateSearchRemarksUpdate(index, newRemarks);
          // 親コンポーネントへの通知
          if (onRemarksChange) {
            onRemarksChange(index, newRemarks);
          }
        }}
        onAdditionalFieldsChange={(newAdditionalFields) => {
          setCurrentAdditionalFields(newAdditionalFields);
          // 同期イベントを発火
          dispatchRealEstateSearchAdditionalFieldsUpdate(
            index,
            newAdditionalFields
          );
          // 親コンポーネントへの通知
          if (onAdditionalFieldsChange) {
            onAdditionalFieldsChange(index, newAdditionalFields);
          }
        }}
        availableFields={availableFields}
        hasAvailableFields={hasAvailableFields}
      />
      <OtherButtonModal
        open={otherButtonModalOpen}
        onClose={() => {
          setOtherButtonModalOpen(false);
        }}
        kaokuNumberSearchFlag={kaokuNumberSearchFlag}
        handleKaokuNumberSearch={
          hasRequiredLocationInfo
            ? handleClickKaokuNumberSearchButton
            : undefined
        }
        handlePinSave={handlePinSave}
        realEstateDocumentFetchFlag={realEstateDocumentFetchFlag}
        handleRealEstateDocumentFetchModal={handleRealEstateDocumentFetch}
        handleRealEstateResearchRequestModal={handleRealEstateResearchRequest}
        propertySummary={propertySummary}
        handleNewPropertySheet={handleCreatePropertySheet}
        googleMapsUrl={googleMapsUrl}
        chikaMapUrl={priceMap ? chikaMapUrl : undefined}
      />
    </Box>
  );
};

/**
 * 不動産検索結果のポップアップを生成する関数
 * @param plot 不動産検索結果データを取得する関数
 * @return L.Popup
 */
const createRealEstateSearchPopup = (
  map: L.Map,
  getItem: () => RealEstateSearchResult,
  index: number,
  kaokuNumberSearchFlag: boolean,
  handleKaokuNumberSearch?: (chibanList: ChibanInput[]) => void,
  onTagsChange?: (index: number, tags: string[]) => void,
  onRemarksChange?: (index: number, remarks: string) => void,
  onAdditionalFieldsChange?: (index: number, additionalFields: string) => void,
  handlePinSave?: () => void,
  crm = false,
  availableFields?: AdditionalFieldConfig,
  hasAvailableFields = false,
  realEstateDocumentFetchFlag = false,
  handleRealEstateDocumentFetchModal?: RealEstateRequestModalHandler,
  handleRealEstateResearchRequestModal?: RealEstateRequestModalHandler,
  propertySummary = false,
  handleNewPropertySheet?: (adi: AddressIntegrator) => void,
  hotelSearch = false,
  dormitorySearch = false,
  priceMap = false,
  industrialFacilitySearch = false,
  loadLandAddress?: LoadLandAddress
): L.Popup => {
  // popupの内容として表示するHTMLを生成する
  const popupHTML = document.createElement("div");
  let root: ReturnType<typeof createRoot> | null = null;

  // ポップアップを開く時に最新のデータでレンダリングする関数
  const renderPopup = (): void => {
    const item = getItem(); // 最新のデータを取得

    // 地図を表示しているコンテナの横幅
    const mapContainerSizeX = map.getSize().x;

    // ポップアップのデフォルトサイズ(px)
    let popupWidth = 300;

    // 地図コンテナ幅375px以下では（300px幅のポップアップ＋余白）がきれいに収まらないので、ポップアップ幅を縮小する
    if (mapContainerSizeX < 375) {
      // PopupContent.tsxと同じに0.8倍にすると家屋番号検索ボタンが改行されてしまうため、0.85倍に変更
      popupWidth = Math.floor(mapContainerSizeX * 0.85);
    }

    if (!root) {
      root = createRoot(popupHTML);
    }

    root.render(
      <ThemeProvider theme={theme}>
        <RealEstateSearchPopupContent
          map={map}
          item={item}
          index={index}
          kaokuNumberSearchFlag={kaokuNumberSearchFlag}
          handleKaokuNumberSearch={handleKaokuNumberSearch}
          onTagsChange={onTagsChange}
          onRemarksChange={onRemarksChange}
          onAdditionalFieldsChange={onAdditionalFieldsChange}
          handlePinSave={handlePinSave}
          crm={crm}
          popupWidth={popupWidth}
          availableFields={availableFields}
          hasAvailableFields={hasAvailableFields}
          realEstateDocumentFetchFlag={realEstateDocumentFetchFlag}
          handleRealEstateDocumentFetchModal={
            handleRealEstateDocumentFetchModal
          }
          handleRealEstateResearchRequestModal={
            handleRealEstateResearchRequestModal
          }
          propertySummary={propertySummary}
          handleNewPropertySheet={handleNewPropertySheet}
          priceMap={priceMap}
          hotelSearchFlag={hotelSearch}
          dormitorySearchFlag={dormitorySearch}
          industrialFacilitySearchFlag={industrialFacilitySearch}
          loadLandAddress={loadLandAddress}
          onLayoutChange={updatePopupLayout}
        />
      </ThemeProvider>
    );
  };
  // 開いている間だけ測り直す（閉じている間は地図に載っていない）
  const updatePopupLayout = (): void => {
    if (popup.isOpen()) popup.update();
  };

  const popup = L.popup({
    content: popupHTML,
  });

  // ポップアップが開く時に最新データでレンダリング & タグ/備考/追加項目更新イベントを発火。
  // 中身は開いた時に初めて作る（最大999件分を最初に作ると重いため）
  popup.on("add", () => {
    const item = getItem();
    // PropertySelectionGridで変更されたタグ/備考/追加項目を反映するためにイベントを発火
    dispatchRealEstateSearchTagUpdate(index, item.tags ?? []);
    dispatchRealEstateSearchRemarksUpdate(index, item.remarks ?? "");
    dispatchRealEstateSearchAdditionalFieldsUpdate(
      index,
      item.additionalFields ?? ""
    );
    // Leaflet は "add" の前に中身が空のままポップアップの幅・位置を決めてしまうため、
    // 同期的に描画してから測り直す
    flushSync(renderPopup);
    popup.update();
  });

  return popup;
};

/**
 * 不動産検索結果プロットのマーカーを生成し、クリックイベントを追加する関数。
 * ピンは地図で共有する1枚の canvas に描く（最大999件を DOM のマーカーで描くと地図が固まるため）
 * @param getItem 最新の不動産検索結果データを取得する関数
 * @return CanvasPinMarker
 */
const createRealEstateSearchMarker = (
  map: L.Map,
  getItem: () => RealEstateSearchResult,
  index: number,
  kaokuNumberSearchFlag: boolean,
  handleKaokuNumberSearch?: (chibanList: ChibanInput[]) => void,
  onTagsChange?: (index: number, tags: string[]) => void,
  onRemarksChange?: (index: number, remarks: string) => void,
  onAdditionalFieldsChange?: (index: number, additionalFields: string) => void,
  onPinClick?: (index: number) => void,
  handlePinSave?: () => void,
  crm = false,
  availableFields?: AdditionalFieldConfig,
  hasAvailableFields = false,
  realEstateDocumentFetchFlag = false,
  handleRealEstateDocumentFetchModal?: RealEstateRequestModalHandler,
  handleRealEstateResearchRequestModal?: RealEstateRequestModalHandler,
  propertySummary = false,
  handleNewPropertySheet?: (adi: AddressIntegrator) => void,
  hotelSearch = false,
  dormitorySearch = false,
  priceMap = false,
  industrialFacilitySearch = false,
  loadLandAddress?: LoadLandAddress
): CanvasPinMarker => {
  const RealEstateSearchPopup = createRealEstateSearchPopup(
    map,
    getItem,
    index,
    kaokuNumberSearchFlag,
    handleKaokuNumberSearch,
    onTagsChange,
    onRemarksChange,
    onAdditionalFieldsChange,
    handlePinSave,
    crm,
    availableFields,
    hasAvailableFields,
    realEstateDocumentFetchFlag,
    handleRealEstateDocumentFetchModal,
    handleRealEstateResearchRequestModal,
    propertySummary,
    handleNewPropertySheet,
    hotelSearch,
    dormitorySearch,
    priceMap,
    industrialFacilitySearch,
    loadLandAddress
  );

  const plot = getItem();
  const marker = new CanvasPinMarker([plot.latitude, plot.longitude], {
    icon: getTeardropPinIcon(PIN_COLORS.realEstateSearch),
    // クリックすると物件一覧に入るので、選択中は一覧の青で描く
    selectedIcon: getTeardropPinIcon(
      onPinClick ? PIN_COLORS.listed : PIN_COLORS.realEstateSearch,
      { selected: true }
    ),
    renderer: getSharedCanvasPinRenderer(map),
    // 一覧の行（handleRealEstateSearchPinClick で作る）と同じ物件として突き合わせる
    property: {
      bookType: plot.bookType,
      prefName: plot.prefName,
      location: plot.location,
      chibanKaokuNumber: plot.chibanKaokuNumber,
    },
  });

  marker.bindPopup(RealEstateSearchPopup);

  marker.on("click", () => {
    // ピンクリック時にリストへ自動追加
    if (onPinClick) {
      onPinClick(index);
    }
    marker.openPopup();
  });
  return marker;
};
